"use client";

import { useState, useEffect, useContext } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { useInternalAuth } from "@/providers/auth-provider";
import { LaunchDarklyObservabilityContext } from "@/components/launchdarkly-observability-provider";
import { downgradeAttributionToSession } from "@/lib/attribution/attribution-cookies";
import { WEB_SHELL_COPY } from "@/components/web-shell.copy";
import { LEGAL_PAGE_PATHS } from "@tami/core/auth";

export function CookieConsent() {
  const { user, isSignedIn } = useInternalAuth();
  const { identify } = useContext(LaunchDarklyObservabilityContext);

  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("cookieConsent") === null) {
      setIsVisible(true);
    }

    const runIdentify = async () => {
      if (isSignedIn && user?.id) {
        const email = user.email?.trim() ?? "";
        const firstName = user.firstName?.trim() ?? "";
        const lastName = user.lastName?.trim() ?? "";
        const displayName = [firstName, lastName].filter(Boolean).join(" ").trim();
        await identify({
          kind: "user",
          key: user.id,
          ...(email ? { email } : {}),
          name: displayName || firstName || email || user.id,
        });
        return;
      }
      if (typeof window !== "undefined") {
        await identify({
          kind: "user",
          key: "guest",
          anonymous: true,
          name: "convidado",
        });
      }
    };
    void runIdentify();
  }, [isSignedIn, user, identify]);

  const handleAccept = () => {
    localStorage.setItem("cookieConsent", "accepted");
    setIsVisible(false);
    window.location.reload();
  };

  const handleDecline = () => {
    localStorage.setItem("cookieConsent", "declined");
    // A origem gravada antes da recusa cai de 90 dias para a sessão atual.
    downgradeAttributionToSession();
    setIsVisible(false);
    // Recarrega para que o PostHog, já inicializado nesta sessão, pare de enviar.
    window.location.reload();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed z-[100] bottom-0 left-0 right-0 bg-background border-t border-border p-4 md:flex md:items-center md:justify-between shadow-lg">
      <div className="mb-4 md:mb-0 md:mr-4">
        <p className="text-sm text-foreground">
          {WEB_SHELL_COPY.cookies.before}{" "}
          <Link
            href={LEGAL_PAGE_PATHS.privacyPolicy}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 hover:text-primary"
          >
            {WEB_SHELL_COPY.cookies.privacyLink}
          </Link>
          .
        </p>
      </div>
      <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
        <Button
          onClick={handleAccept}
          className="w-full sm:w-auto"
          data-testid="cookieConsent-btn-accepted"
        >
          {WEB_SHELL_COPY.cookies.accept}
        </Button>
        <Button
          onClick={handleDecline}
          variant="outline"
          className="w-full sm:w-auto"
          data-testid="cookieConsent-btn-rejected"
        >
          {WEB_SHELL_COPY.cookies.decline}
        </Button>
        <Button
          data-testid="cookieConsent-btn-close"
          variant="ghost"
          size="icon"
          className="absolute top-2 right-2 md:hidden"
          onClick={handleDecline}
          aria-label={WEB_SHELL_COPY.cookies.close}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
